/**
 * Standardized error handling utilities
 */

import { createComponentLogger } from './logger';
import { FirebaseError } from '../types/errors';

const logger = createComponentLogger('ErrorHandler');

/**
 * Standard application error class
 */
export class AppError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode: number = 500,
    public originalError?: Error
  ) {
    super(message);
    this.name = 'AppError';
  }
}

/**
 * Validation error class
 */
export class ValidationError extends AppError {
  constructor(message: string, public field?: string) {
    super(message, 'VALIDATION_ERROR', 400);
    this.name = 'ValidationError';
  }
}

/**
 * Authorization error class
 */
export class AuthorizationError extends AppError {
  constructor(message: string = 'Access denied') {
    super(message, 'AUTHORIZATION_ERROR', 403);
    this.name = 'AuthorizationError';
  }
}

/**
 * Not found error class
 */
export class NotFoundError extends AppError {
  constructor(message: string = 'Resource not found') {
    super(message, 'NOT_FOUND_ERROR', 404);
    this.name = 'NotFoundError';
  }
}

/**
 * Type guards for different error types
 */
export const isFirebaseError = (error: unknown): error is FirebaseError => {
  if (error === null || typeof error !== 'object' || !('code' in error)) {
    return false;
  }
  const code = (error as { code: unknown }).code;
  return typeof code === 'string' &&
         (code.startsWith('auth/') ||
          code.startsWith('firestore/') ||
          code.startsWith('functions/'));
};

export const isAppError = (error: unknown): error is AppError => {
  return error instanceof AppError;
};

export const isValidationError = (error: unknown): error is ValidationError => {
  return error instanceof ValidationError;
};

/**
 * Safely get error message from unknown error
 */
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  
  if (typeof error === 'string') {
    return error;
  }
  
  if (error !== null && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  
  return 'An unknown error occurred';
};

/**
 * Safely get error code from unknown error
 */
export const getErrorCode = (error: unknown): string => {
  if (error instanceof AppError) {
    return error.code;
  }
  
  if (error !== null && typeof error === 'object' && 'code' in error) {
    return String((error as { code: unknown }).code);
  }
  
  return 'UNKNOWN_ERROR';
};

/**
 * Convert Firebase errors to user-friendly messages
 */
export const handleFirebaseError = (error: unknown): AppError => {
  if (!isFirebaseError(error)) {
    return new AppError(getErrorMessage(error), 'UNKNOWN_ERROR', 500, error as Error);
  }

  logger.error('Firebase error:', error);

  // Auth errors
  if (error.code.startsWith('auth/')) {
    switch (error.code) {
      case 'auth/user-not-found':
        return new AppError('No account found with this email', 'USER_NOT_FOUND', 404);
      case 'auth/wrong-password':
        return new AppError('Incorrect password', 'INVALID_PASSWORD', 401);
      case 'auth/invalid-email':
        return new AppError('Invalid email address', 'INVALID_EMAIL', 400);
      case 'auth/email-already-in-use':
        return new AppError('An account with this email already exists', 'EMAIL_IN_USE', 409);
      case 'auth/weak-password':
        return new AppError('Password is too weak', 'WEAK_PASSWORD', 400);
      case 'auth/too-many-requests':
        return new AppError('Too many failed attempts. Please try again later', 'TOO_MANY_REQUESTS', 429);
      case 'auth/network-request-failed':
        return new AppError('Network error. Please check your connection', 'NETWORK_ERROR', 503);
      default:
        return new AppError(error.message || 'Authentication error', 'AUTH_ERROR', 401);
    }
  }

  // Firestore errors
  if (error.code.startsWith('firestore/')) {
    switch (error.code) {
      case 'firestore/permission-denied':
        return new AuthorizationError('Permission denied');
      case 'firestore/not-found':
        return new NotFoundError('Document not found');
      case 'firestore/unavailable':
        return new AppError('Service temporarily unavailable', 'SERVICE_UNAVAILABLE', 503);
      case 'firestore/deadline-exceeded':
        return new AppError('Request timed out', 'TIMEOUT', 408);
      default:
        return new AppError(error.message || 'Database error', 'DATABASE_ERROR', 500);
    }
  }

  // Functions errors
  if (error.code.startsWith('functions/')) {
    switch (error.code) {
      case 'functions/unauthenticated':
        return new AppError('Authentication required', 'UNAUTHENTICATED', 401);
      case 'functions/permission-denied':
        return new AuthorizationError('Permission denied');
      case 'functions/unavailable':
        return new AppError('Service temporarily unavailable', 'SERVICE_UNAVAILABLE', 503);
      case 'functions/internal':
        return new AppError('Internal server error', 'INTERNAL_ERROR', 500);
      default:
        return new AppError(error.message || 'Function error', 'FUNCTION_ERROR', 500);
    }
  }

  return new AppError(error.message || 'Unknown Firebase error', 'FIREBASE_ERROR', 500);
};

/**
 * Wrap async operations with standardized error handling
 */
export const withErrorHandling = async <T>(
  operation: () => Promise<T>,
  context: string
): Promise<T> => {
  try {
    return await operation();
  } catch (error: unknown) {
    logger.error(`Error in ${context}:`, error);
    
    if (isFirebaseError(error)) {
      throw handleFirebaseError(error);
    }
    
    if (isAppError(error)) {
      throw error;
    }
    
    throw new AppError(
      getErrorMessage(error),
      'UNKNOWN_ERROR',
      500,
      error as Error
    );
  }
};

/**
 * Create a standardized error response for UI
 */
export const createErrorResponse = (error: unknown) => {
  const appError = isAppError(error) ? error : handleFirebaseError(error);
  
  return {
    success: false,
    error: {
      message: appError.message,
      code: appError.code,
      statusCode: appError.statusCode
    }
  };
};